import { useEffect, useMemo, useState } from "react";
import { api, type OrganizationSummary } from "@/lib/api";
import { formatarDataHora } from "@/lib/auditLog";
import {
  addResultSummary,
  DICTIONARY_KIND_LABELS,
  DICTIONARY_LANGUAGES,
  dictionaryCsv,
  dictionaryKey,
  dictionaryLanguageLabel,
  entriesForScope,
  parseDictionaryInput,
  type DictionaryAddSummary,
  type DictionaryEntry,
  type DictionaryKind,
  type DictionaryLanguage,
  type ScopedDictionaryEntry,
} from "@/lib/spellingDictionary";

const CONTROLE =
  "rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs text-ink-200 focus:outline-none focus:ring-1 focus:ring-ink-500";
const BOTAO =
  "rounded border border-ink-700 bg-ink-800 px-3 py-1 text-xs text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:cursor-not-allowed disabled:opacity-40";

const COLUNAS: Record<DictionaryKind, string> = {
  accepted: "minmax(8rem,1.3fr) 6rem minmax(8rem,1.2fr) minmax(9rem,1.3fr) 7.5rem 6rem",
  blocked: "minmax(7rem,1.1fr) 6rem minmax(6rem,1fr) minmax(8rem,1.2fr) minmax(9rem,1.2fr) 7.5rem 6rem",
};

const DICA: Record<DictionaryKind, string> = {
  accepted: "Uma palavra por linha (vírgula, «、» e «，» também separam). Maiúsculas não importam; acento sim.",
  blocked: "Uma por linha, com a correção depois do «=» — ex.: email = e-mail. A correção é opcional.",
};

function mensagemDe(erro: unknown, padrao: string): string {
  return erro instanceof Error && erro.message ? erro.message : padrao;
}

function hoje(): string {
  const agora = new Date();
  const dois = (n: number) => String(n).padStart(2, "0");
  return `${agora.getFullYear()}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}`;
}

const nomeDa = (org: OrganizationSummary | null | undefined) => (org ? org.name || org.id : "Todas as organizações");

/**
 * Dicionário: as palavras que a ortografia do viewer aceita ou acusa (out/2026).
 *
 * Por organização (21/10/2026, layout escolhido pelo dono): à esquerda, o global
 * e as empresas do LinkFlow; à direita, as palavras do escopo escolhido. Numa
 * empresa, a palavra vale só nos links dela e no gerenciador, nas artes dela —
 * somada às globais, e vencendo a global quando as duas dizem coisas opostas.
 * O cliente aceita pelo link («pelo link» na coluna); remover é só daqui. A
 * régua (idiomas, o que foi colado, filtro, CSV, escopo) mora em
 * `lib/spellingDictionary.ts`.
 */
export function Dictionary() {
  const [entradas, setEntradas] = useState<DictionaryEntry[]>([]);
  const [organizacoes, setOrganizacoes] = useState<OrganizationSummary[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);
  /** `null` é o dicionário global. */
  const [escopo, setEscopo] = useState<string | null>(() => new URLSearchParams(window.location.search).get("org"));
  const [buscaDeEmpresa, setBuscaDeEmpresa] = useState("");
  const [comGlobais, setComGlobais] = useState(true);
  const [aba, setAba] = useState<DictionaryKind>("accepted");
  const [filtroIdioma, setFiltroIdioma] = useState<DictionaryLanguage | "">("");
  const [busca, setBusca] = useState("");
  const [idiomaNovo, setIdiomaNovo] = useState<DictionaryLanguage>("pt");
  const [texto, setTexto] = useState("");
  const [gravando, setGravando] = useState(false);
  const [resumo, setResumo] = useState<DictionaryAddSummary | null>(null);
  // Remover é em dois cliques: o primeiro arma a linha, o segundo apaga.
  const [armada, setArmada] = useState<string | null>(null);
  const [removendo, setRemovendo] = useState<string | null>(null);

  useEffect(() => {
    const controle = new AbortController();
    setCarregando(true);
    setErro(null);
    Promise.all([api.listSpellingDictionary(controle.signal), api.listOrganizations(controle.signal)])
      .then(([lista, empresas]) => {
        setEntradas(lista.items);
        setOrganizacoes(empresas.items);
      })
      .catch((falha) => {
        if (!controle.signal.aborted) setErro(mensagemDe(falha, "Não foi possível carregar o dicionário."));
      })
      .finally(() => {
        if (!controle.signal.aborted) setCarregando(false);
      });
    return () => controle.abort();
  }, [recarga]);

  const empresa = escopo ? organizacoes.find((org) => org.id === escopo) ?? null : null;
  const doEscopo = useMemo(() => entriesForScope(entradas, escopo, comGlobais), [comGlobais, entradas, escopo]);
  const contagem = useMemo(() => {
    const proprias = doEscopo.filter((linha) => !linha.global);
    return {
      accepted: proprias.filter((linha) => linha.entry.kind === "accepted").length,
      blocked: proprias.filter((linha) => linha.entry.kind === "blocked").length,
    };
  }, [doEscopo]);
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase("pt-BR");
    return doEscopo
      .filter((linha) => linha.entry.kind === aba)
      .filter((linha) => !filtroIdioma || linha.entry.language === filtroIdioma)
      .filter(
        (linha) =>
          !termo ||
          dictionaryKey(linha.entry.word).includes(termo) ||
          (linha.entry.correction ?? "").toLocaleLowerCase("pt-BR").includes(termo),
      )
      .sort(
        (a, b) =>
          Number(a.global) - Number(b.global) ||
          a.entry.word.localeCompare(b.entry.word, "pt-BR", { sensitivity: "base" }) ||
          dictionaryLanguageLabel(a.entry.language).localeCompare(dictionaryLanguageLabel(b.entry.language), "pt-BR"),
      );
  }, [aba, busca, doEscopo, filtroIdioma]);
  const pedidos = useMemo(() => parseDictionaryInput(texto, aba), [aba, texto]);
  const palavrasPorEscopo = useMemo(() => {
    const mapa = new Map<string | null, number>();
    for (const entrada of entradas) {
      const chave = entrada.organization_id ?? null;
      mapa.set(chave, (mapa.get(chave) ?? 0) + 1);
    }
    return mapa;
  }, [entradas]);
  const empresasVisiveis = useMemo(() => {
    const termo = buscaDeEmpresa.trim().toLocaleLowerCase("pt-BR");
    return organizacoes.filter((org) => !termo || (org.name ?? "").toLocaleLowerCase("pt-BR").includes(termo) || org.id.toLocaleLowerCase("pt-BR").includes(termo));
  }, [buscaDeEmpresa, organizacoes]);

  // Trocar de aba esvazia a caixa: o formato das duas é outro (vírgula separa
  // as aceitas; «=» marca a correção das bloqueadas), e a sobra de uma vira
  // palavra inválida na outra.
  const trocarAba = (proxima: DictionaryKind) => {
    setAba(proxima);
    setTexto("");
    setResumo(null);
    setArmada(null);
  };

  const trocarEscopo = (proximo: string | null) => {
    setEscopo(proximo);
    setResumo(null);
    setArmada(null);
  };

  const adicionar = async () => {
    if (!pedidos.length || gravando) return;
    setGravando(true);
    setResumo(null);
    try {
      const resultado = await api.addToSpellingDictionary({
        kind: aba,
        language: idiomaNovo,
        entries: pedidos,
        organization_id: escopo,
      });
      setEntradas((lista) => [...lista, ...resultado.added]);
      setResumo(addResultSummary(resultado));
      // Palavra recusada por não ser palavra fica na caixa para corrigir; o
      // resto (entrou, ou já estava) sai.
      if (!resultado.skipped.some((recusa) => recusa.reason === "invalid")) setTexto("");
    } catch (falha) {
      setResumo({ headline: mensagemDe(falha, "Não foi possível gravar no dicionário."), details: [] });
    } finally {
      setGravando(false);
    }
  };

  const remover = async (entrada: DictionaryEntry) => {
    if (armada !== entrada.id) {
      setArmada(entrada.id);
      return;
    }
    setRemovendo(entrada.id);
    try {
      await api.removeFromSpellingDictionary(entrada.id);
      setEntradas((lista) => lista.filter((item) => item.id !== entrada.id));
    } catch (falha) {
      setErro(mensagemDe(falha, "Não foi possível remover a palavra."));
    } finally {
      setRemovendo(null);
      setArmada(null);
    }
  };

  const exportar = () => {
    const ordenadas = [...entradas].sort(
      (a, b) =>
        (a.organization_name ?? a.organization_id ?? "").localeCompare(b.organization_name ?? b.organization_id ?? "", "pt-BR") ||
        a.kind.localeCompare(b.kind) ||
        a.word.localeCompare(b.word, "pt-BR", { sensitivity: "base" }),
    );
    // O BOM faz o Excel ler o UTF-8 sem perguntar — senão «Português» vira lixo.
    const blob = new Blob(["﻿" + dictionaryCsv(ordenadas)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dicionario-linkflow-${hoje()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const colunas = COLUNAS[aba];
  const temFiltro = Boolean(filtroIdioma || busca.trim());
  const alvo = escopo ? nomeDa(empresa) || escopo : "todas";
  const totalGlobal = palavrasPorEscopo.get(null) ?? 0;

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between border-b border-ink-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink-100">Dicionário</h1>
          <p className="mt-0.5 text-xs text-ink-400">
            Palavras que a ortografia do viewer aceita ou acusa — para todas as organizações ou só para uma.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportar} disabled={!entradas.length} className={BOTAO}>
            Exportar CSV
          </button>
          <button
            type="button"
            onClick={() => setRecarga((n) => n + 1)}
            disabled={carregando}
            className="rounded border border-ink-700 bg-ink-800 px-3 py-1.5 text-xs text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:opacity-50"
          >
            ↺ Atualizar
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-6 py-4 lg:flex-row">
        <aside className="flex max-h-72 shrink-0 flex-col overflow-hidden rounded-lg border border-ink-700 lg:max-h-none lg:w-64" aria-label="Organizações">
          <div className="flex flex-col gap-1.5 border-b border-ink-800 p-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Organizações</span>
            <input
              type="search"
              value={buscaDeEmpresa}
              onChange={(event) => setBuscaDeEmpresa(event.target.value)}
              placeholder="Nome ou id…"
              aria-label="Buscar organização"
              className={`${CONTROLE} px-2.5 placeholder-ink-600`}
            />
          </div>
          <div className="flex-1 overflow-y-auto p-1">
            <button
              type="button"
              onClick={() => trocarEscopo(null)}
              aria-pressed={escopo === null}
              className={`block w-full rounded px-2.5 py-2 text-left ${escopo === null ? "bg-ink-700 text-ink-50" : "text-ink-300 hover:bg-ink-800"}`}
            >
              <span className="block text-sm font-medium">Todas as organizações</span>
              <span className={`block text-[11px] ${escopo === null ? "text-ink-300" : "text-ink-500"}`}>global · {totalGlobal} palavras</span>
            </button>
            {empresasVisiveis.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => trocarEscopo(org.id)}
                aria-pressed={escopo === org.id}
                className={`block w-full rounded px-2.5 py-2 text-left ${escopo === org.id ? "bg-ink-700 text-ink-50" : "text-ink-300 hover:bg-ink-800"}`}
              >
                <span className="block truncate text-sm font-medium" title={org.name ?? org.id}>
                  {nomeDa(org)}
                </span>
                <span className={`block truncate text-[11px] ${escopo === org.id ? "text-ink-300" : "text-ink-500"}`}>
                  {palavrasPorEscopo.get(org.id) ?? 0} palavras · {org.links_30d} links em 30 dias
                </span>
              </button>
            ))}
            {!organizacoes.length && !carregando ? (
              <p className="px-2.5 py-3 text-[11px] text-ink-500">
                Nenhuma empresa ainda: elas aparecem quando o LinkFlow manda o cabeçalho de organização no link.
              </p>
            ) : null}
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-hidden">
          <div className="flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-base font-semibold text-ink-100">{escopo ? nomeDa(empresa) || escopo : "Todas as organizações"}</h2>
            {escopo ? (
              <>
                <code className="font-mono text-[11px] text-ink-500">{escopo}</code>
                <label className="ml-auto flex items-center gap-1.5 text-xs text-ink-300">
                  <input type="checkbox" checked={comGlobais} onChange={(event) => setComGlobais(event.target.checked)} />
                  Mostrar as globais junto
                </label>
              </>
            ) : (
              <span className="text-xs text-ink-500">dicionário global</span>
            )}
          </div>
          <p className="shrink-0 text-xs text-ink-400">
            {escopo ? (
              <>
                Valem só nos links de <span className="text-ink-200">{nomeDa(empresa) || escopo}</span> e no gerenciador, nas artes dela — somadas às globais. Se a mesma palavra estiver nas duas listas, <span className="text-ink-200">vale a da organização</span>.
              </>
            ) : (
              <>
                Valem em todos os links e no gerenciador. Uma organização pode ter regra própria para a mesma palavra — nos links dela, vale a dela.
              </>
            )}
          </p>

          <div className="flex shrink-0 items-center gap-1 border-b border-ink-800" role="tablist" aria-label="Listas do dicionário">
            {(["accepted", "blocked"] as const).map((tipo) => (
              <button
                key={tipo}
                type="button"
                role="tab"
                aria-selected={aba === tipo}
                onClick={() => trocarAba(tipo)}
                className={`-mb-px border-b-2 px-3 py-1.5 text-xs font-medium ${
                  aba === tipo ? "border-ink-200 text-ink-100" : "border-transparent text-ink-400 hover:text-ink-200"
                }`}
              >
                {DICTIONARY_KIND_LABELS[tipo]}{" "}
                <span className="ml-0.5 rounded-full bg-ink-800 px-1.5 py-0.5 text-[10px] tabular-nums text-ink-300">{contagem[tipo]}</span>
              </button>
            ))}
          </div>

          <div className="shrink-0 space-y-2 rounded-lg border border-ink-700 bg-ink-850 p-3">
            <div className="flex flex-wrap items-start gap-2">
              <label className="flex items-center gap-2">
                <span className="text-xs font-medium text-ink-400">Idioma:</span>
                <select
                  value={idiomaNovo}
                  onChange={(event) => setIdiomaNovo(event.target.value as DictionaryLanguage)}
                  className={CONTROLE}
                  aria-label="Idioma das palavras a adicionar"
                >
                  {DICTIONARY_LANGUAGES.map((idioma) => (
                    <option key={idioma.code} value={idioma.code}>
                      {idioma.label}
                    </option>
                  ))}
                </select>
              </label>
              <textarea
                value={texto}
                onChange={(event) => setTexto(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) void adicionar();
                }}
                rows={3}
                placeholder={aba === "accepted" ? "mockup\ninstagram" : "email = e-mail"}
                aria-label={aba === "accepted" ? "Palavras a aceitar" : "Palavras a bloquear"}
                className={`min-w-[16rem] flex-1 resize-y ${CONTROLE} px-2.5 py-1.5 font-mono placeholder-ink-600`}
              />
              <button
                type="button"
                onClick={() => void adicionar()}
                disabled={!pedidos.length || gravando}
                className="rounded border border-ink-500 bg-ink-700 px-3 py-1.5 text-xs font-medium text-ink-100 hover:bg-ink-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {gravando
                  ? "Gravando…"
                  : pedidos.length > 1
                    ? `Adicionar ${pedidos.length} palavras`
                    : aba === "accepted"
                      ? `Aceitar para ${alvo}`
                      : `Bloquear para ${alvo}`}
              </button>
            </div>
            <p className="text-[11px] text-ink-500">{DICA[aba]}</p>
            {resumo && (
              <div className="text-xs" role="status">
                <p className="text-ink-200">{resumo.headline}</p>
                {resumo.details.length > 0 && (
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-ink-400">
                    {resumo.details.map((linha) => (
                      <li key={linha}>{linha}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-ink-400">Idioma:</span>
            <select
              value={filtroIdioma}
              onChange={(event) => setFiltroIdioma(event.target.value as DictionaryLanguage | "")}
              className={CONTROLE}
              aria-label="Filtrar por idioma"
            >
              <option value="">Todos</option>
              {DICTIONARY_LANGUAGES.map((idioma) => (
                <option key={idioma.code} value={idioma.code}>
                  {idioma.label}
                </option>
              ))}
            </select>
            <span className="text-xs font-medium text-ink-400">Busca:</span>
            <input
              type="search"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              maxLength={64}
              placeholder="Palavra ou correção…"
              className={`min-w-[12rem] ${CONTROLE} px-2.5 placeholder-ink-600`}
              aria-label="Buscar palavra"
            />
            {temFiltro && (
              <button
                type="button"
                onClick={() => {
                  setFiltroIdioma("");
                  setBusca("");
                }}
                className={`ml-auto ${BOTAO}`}
              >
                Limpar filtros
              </button>
            )}
          </div>

          {erro && (
            <p className="flex shrink-0 items-center justify-between rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
              <span>{erro}</span>
              <button type="button" onClick={() => setRecarga((n) => n + 1)} className={BOTAO}>
                Tentar de novo
              </button>
            </p>
          )}

          <div className="flex min-h-[12rem] flex-1 flex-col overflow-hidden rounded-lg border border-ink-700">
            <div className="flex-1 overflow-auto">
              <div className="min-w-[46rem]">
                <div
                  className="sticky top-0 grid select-none gap-x-3 border-b border-ink-700 bg-ink-850 px-3 py-2 text-xs font-medium uppercase tracking-wide text-ink-400"
                  style={{ gridTemplateColumns: colunas }}
                >
                  <span>Palavra</span>
                  <span>Idioma</span>
                  {aba === "blocked" && <span>Correção</span>}
                  <span>Vale para</span>
                  <span>Adicionada por</span>
                  <span>Em</span>
                  <span />
                </div>
                {carregando && entradas.length === 0 && (
                  <div className="flex items-center justify-center py-12 text-sm text-ink-500">Carregando…</div>
                )}
                {!carregando && !erro && visiveis.length === 0 && (
                  <div className="flex items-center justify-center px-6 py-12 text-center text-sm text-ink-500">
                    {temFiltro
                      ? "Nenhuma palavra com esse filtro."
                      : escopo
                        ? `Nenhuma palavra só de ${nomeDa(empresa) || escopo} nesta lista ainda; valem as globais.`
                        : aba === "accepted"
                          ? "Nenhuma palavra aceita ainda. Use «aceitar» no erro, no viewer, ou adicione aqui."
                          : "Nenhuma palavra bloqueada ainda."}
                  </div>
                )}
                {visiveis.map((linha) => (
                  <LinhaDoDicionario
                    key={`${linha.global ? "g" : "o"}:${linha.entry.id}`}
                    linha={linha}
                    aba={aba}
                    colunas={colunas}
                    armada={armada}
                    removendo={removendo}
                    onRemover={(entrada) => void remover(entrada)}
                    onDesarmar={(id) => setArmada((atual) => (atual === id ? null : atual))}
                  />
                ))}
              </div>
            </div>
            <div className="border-t border-ink-700 bg-ink-850 px-3 py-2 text-xs text-ink-400">
              {contagem[aba]} {contagem[aba] === 1 ? "palavra" : "palavras"} {escopo ? `de ${nomeDa(empresa) || escopo}` : "globais"}
              {escopo && comGlobais ? ` · ${visiveis.filter((l) => l.global).length} globais mostradas junto` : ""}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LinhaDoDicionario({
  linha,
  aba,
  colunas,
  armada,
  removendo,
  onRemover,
  onDesarmar,
}: {
  linha: ScopedDictionaryEntry;
  aba: DictionaryKind;
  colunas: string;
  armada: string | null;
  removendo: string | null;
  onRemover: (entrada: DictionaryEntry) => void;
  onDesarmar: (id: string) => void;
}) {
  const { entry: entrada, global: daGlobal, overriddenBy } = linha;
  const valePara = daGlobal ? (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="rounded-full border border-ink-700 px-1.5 text-[10px] text-ink-400">Global</span>
      {overriddenBy ? (
        <span className="text-[11px] text-ink-500">
          não vale aqui: a organização {overriddenBy.kind === "accepted" ? "aceita" : "bloqueia"}
        </span>
      ) : null}
    </span>
  ) : entrada.organization_id ? (
    <span className="max-w-full justify-self-start truncate rounded-full border border-cyan-900 px-1.5 text-[10px] text-cyan-200" title={entrada.organization_id}>
      {entrada.organization_name ?? entrada.organization_id}
    </span>
  ) : (
    <span className="justify-self-start rounded-full border border-ink-700 px-1.5 text-[10px] text-ink-300">Global</span>
  );
  return (
    <div
      className={`grid items-center gap-x-3 border-b border-ink-800 px-3 py-2 text-xs last:border-b-0 ${daGlobal ? "text-ink-500" : ""}`}
      style={{ gridTemplateColumns: colunas }}
    >
      <span className={`truncate font-medium ${daGlobal ? "text-ink-400" : "text-ink-100"}`} title={entrada.word}>
        {entrada.word}
      </span>
      <span className="text-ink-300">{dictionaryLanguageLabel(entrada.language)}</span>
      {aba === "blocked" && (
        <span className="truncate text-ink-200" title={entrada.correction ?? undefined}>
          {entrada.correction ?? <span className="text-ink-600">—</span>}
        </span>
      )}
      {valePara}
      <span className="flex min-w-0 items-center gap-1.5 text-ink-400" title={entrada.created_by ?? undefined}>
        <span className="truncate">{entrada.created_by ?? "—"}</span>
        {entrada.created_via === "link" ? (
          <span className="shrink-0 rounded-full border border-dashed border-ink-600 px-1.5 text-[10px] text-ink-300">pelo link</span>
        ) : null}
      </span>
      <span className="tabular-nums text-ink-400">{entrada.created_at ? formatarDataHora(entrada.created_at) : "—"}</span>
      <span className="flex justify-end">
        {daGlobal ? (
          <span className="text-[11px] text-ink-600">edite em Global</span>
        ) : (
          <button
            type="button"
            onClick={() => onRemover(entrada)}
            onBlur={() => onDesarmar(entrada.id)}
            disabled={removendo !== null}
            className={
              armada === entrada.id
                ? "rounded border border-red-700 bg-red-950/50 px-2 py-0.5 text-[11px] text-red-300 hover:bg-red-900/50"
                : "rounded border border-ink-700 px-2 py-0.5 text-[11px] text-ink-400 hover:border-ink-500 hover:text-ink-100"
            }
          >
            {removendo === entrada.id ? "Removendo…" : armada === entrada.id ? "Confirmar" : "Remover"}
          </button>
        )}
      </span>
    </div>
  );
}
